<template>
  <div style="-webkit-app-region: no-drag">
    <v-btn icon @click="dialog = true">
      <v-icon>mdi-clipboard-text-clock-outline</v-icon>
    </v-btn>

    <v-dialog v-model="dialog" max-width="680" scrollable>
      <v-card>
        <v-toolbar color="primary" dark flat>
          <v-icon left>mdi-clipboard-text-clock-outline</v-icon>
          <span class="text-subtitle-1 font-weight-bold">Patch Notes</span>
          <v-spacer />
          <v-btn icon @click="dialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-toolbar>

        <v-card-text class="pa-0">
          <div v-for="section in sections" :key="section.key">
            <div
              class="px-6 pt-4 pb-2 d-flex align-center"
              :class="section.key === 'previous' ? 'mt-2' : ''"
              style="gap: 8px"
            >
              <v-icon small :color="section.color">
                {{ section.icon }}
              </v-icon>
              <span class="text-overline font-weight-bold" :class="section.textClass">
                {{ section.title }}
              </span>
            </div>
            <v-divider />
            <div
              v-for="(release, i) in section.releases"
              :key="release.version"
            >
              <div class="px-6 pt-5 pb-4">
                <div class="d-flex align-center mb-3" style="gap: 10px">
                  <v-chip color="primary" dark label small>
                    v{{ release.version }}
                  </v-chip>
                  <span class="text-body-2 grey--text">{{ release.date }}</span>
                  <v-chip v-if="release.preRelease" color="orange" dark label small>Pre-release</v-chip>
                  <v-chip v-else-if="isLatest(release)" color="success" dark label small>Latest</v-chip>
                </div>
                <ul style="padding-left: 20px; margin: 0">
                  <li
                    v-for="(note, j) in release.notes"
                    :key="j"
                    style="font-size: 0.95rem; line-height: 1.5; margin-bottom: 10px"
                  >
                    {{ note }}
                  </li>
                </ul>
              </div>
              <v-divider v-if="i < section.releases.length - 1" />
            </div>
          </div>
        </v-card-text>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
export default {
  name: 'PatchNotes',

  data() {
    return {
      dialog: false,
      releases: [
        {
          version: '1.19.6',
          date: '2026-08-13',
          preRelease: true,
          notes: [
            'The Sales Report\'s Disc column now shows the discount in pesos instead of a percentage, so it reads as money like the SRP, Cost and Total columns beside it',
          ],
        },
        {
          version: '1.19.5',
          date: '2026-07-31',
          notes: [
            'The Sales Report now includes credit sales as well as cash sales. Each item is still one row, with its units and revenue covering both, and Gross Revenue and Total Cost now cover both as well',
            'Sales Report rows whose figures include credit sales are marked with "(incl. credit)" after the item name and shaded, so it is clear which items were partly or wholly sold on credit',
            'The Statement of Account now lists every item in a consumer loan - the receipt row comes first with its dates and amounts, and each item follows underneath showing the item name and its amount',
            'Statement of Account invoice amounts and balances now subtract per-item discounts, matching the Collectibles printout and the consumer loan summary',
            'Product Movement, its Excel export, and the Quantity Remaining Breakdown dialog now show a Consumer column - the consumer on the cash sale or credit sale behind the entry, and a dash for purchases, transfers, losses and adjustments',
            'The source document popup now shows the Consumer for sales receipts, promotional items, credit sales and returned products - credit sales show both the Consumer and the Loanee who collected the goods',
            'The manage loans list has a new Amount column showing what was borrowed, sitting between Due and Balance so the row reads amount owed then amount remaining',
            'The loans table column headers now sort - CR #, Name, Loaned, Due, Amount, Balance, Progress, Remark and Receipt all sort across the whole list rather than just the page on screen, and Remark sorts by urgency (no items, fully paid, paid late, not yet due, due today, overdue) instead of alphabetically',
            'The Edit Loan dialog now uses the same form as Add Loan Batch - its first field is labelled CR Number instead of Reciept No., and spaces typed into it are stripped out the same way',
            'Edit Loan Item no longer asks for a Supplier - it now has the same Stock Code, Price and Quantity inputs as Add Item, and its stock search works the same way (type to search every stock in the store instead of first picking a supplier)',
            'Fixed Edit Loan Item opening with empty fields the first time it was used on a page',
            'The Purchases report now lists every purchased item instead of one line per receipt - Receipt No, Product Name, Unit, Qty, Cost, Total (that item\'s cost) and Amount (the whole receipt it belongs to), plus the date of purchase, in both the PDF and the Excel export',
            'The Purchases report PDF headers are abbreviated (Rcpt #, Qty, Amount) and the columns are sized to fit the page, with long product names wrapping instead of overflowing',
            'Consumer loan items can now be given a discount, entered the same way as on Cash Sales - a Discount/Item field with a peso/percent toggle and a Total Discounted Amnt that follows the price and quantity',
            'The loan items list now has a Discount column, and its Total Amount and Total Debt subtract the discount instead of showing the gross price times quantity',
            'The dashboard Printouts panel has a new Customer Reports button that downloads an Excel file of every customer transaction - cash sales and credit sales together, one row per sold item, using the Start and End dates and the Store already set on that panel',
            'The Customer Reports file lists the transaction type, consumer, receipt number, store, product name and stock code, unit, quantity, retail price, discount, amount, the actual date of the transaction, and when the record was created and last updated',
            'Leaving the Start date blank on a Customer Report exports everything from the very first transaction up to the End date, instead of asking for a start date',
            'The printed consumer loan receipt now shows Date Loaned - the date the goods were actually taken on credit. The date at the top of the page is the day the receipt was printed, which is not the same thing and was previously the only date on it',
            'Fixed Amount Paid always printing as 0.00 on the consumer loan receipt - the payments made against the loan now show properly, and Balance follows below it',
            'This Patch Notes dialog is now split into "This Update" and "Previous Releases", so the release you are on is no longer flush against the older history',
            'The Daily Sales Insight report now includes credit sales alongside cash sales and transfers, listed with a type of "credit", so a day with credit trade no longer reads short on revenue, cost and profit',
            'The Daily Sales Insight now costs each line at the average price the product was bought for, the same basis the Sales Report uses, so the two reports no longer disagree on the cost, profit and margin of the same sale. The Purchase Receipt No, Purchase Unit and Purchase Qty columns still show the latest purchase',
          ],
        },
        {
          version: '1.19.4.b',
          date: '2026-07-30',
          notes: [
            'Printing a sale invoice works again - the Print Receipt action on Cash Sales failed with a server error and produced no PDF',
            'The sale invoice PDF now fills in every field: Code and Item per line, plus Receipt No, Consumer, Date, Timestamp and the Total, which previously printed as blanks',
            'Downloaded invoices are named after their receipt number again instead of all saving as sale-receipt-invoice.pdf',
          ],
        },
        {
          version: '1.19.4.a',
          date: '2026-07-29',
          notes: [
            'The Discount/Item and Total Discounted Amnt fields on Cash Sales now each take up a full row, so the per-item discount input is no longer squeezed next to the peso/percent toggle',
          ],
        },
        {
          version: '1.19.4',
          date: '2026-07-17',
          notes: [
            'Selling products with zero or negative stock now requires the new "Sales - Create Zero Stock" permission',
            'Without the permission, the quantity input on Cash Sales is disabled and an error popup explains that the product has no stock left - this also applies to barcode scans',
            'The server now rejects zero-stock sales from users without the permission, even from older app versions',
            'Permission changes now take effect when the user switches pages, without needing to log out and back in',
            'Sale transaction dates are now limited to the current month (up to today) unless the user has the new "Sales - Date Edit" permission - typed dates outside the range snap back to the nearest allowed date, and sale edits keep their original date without the permission',
            'Fixed the quantity error message staying visible after switching to a product that has stock',
            'Expenses can now be edited from Manage Expenses via a new edit button, visible only to users with the "Expenses - Edit" permission (the same permission that already controls expense deletion)',
            'Purchase receipts now show a Paid/Unpaid payment status you can toggle by clicking the chip, available to users with the new "Purchases - Paid Status Edit" permission',
            'Source references in Inventory Monitoring and Product Movement are now clickable links that open a popup showing the underlying document (purchase receipt, sale, transfer, loan, and more)',
            'Added a Purchases report (receipt number, total amount, and date) with a date-range filter, exportable to Excel and PDF, from the Purchases page and the transactions filter row',
            'The Cash Sales Discount/Item field now has a peso/percent toggle so per-item discounts can be entered as either an amount or a percentage',
            'Product Movement, its Excel export, and the Quantity Remaining Breakdown dialog now show an Actual Date column with each entry\'s source document date',
            'Added a Collectibles printout on the dashboard - a PDF of each receipt\'s outstanding balance (receivable, paid, and balance) as of today, grouped by consumer, with grand totals',
            'Added a Company Settings page under Admin for setting the company name, logo, and the statement approver\'s name and position, with a placeholder shown when none has been set yet',
            'Added a Statement of Account printout per consumer on the Manage Consumers page - a PDF showing the company logo and name, the consumer\'s deliveries, due dates, invoices, payments, balances and days overdue, with total credit and total due, plus a conforme and signatory section with the configured approver',
          ],
        },
        {
          version: '1.19.2.a',
          date: '2026-07-04',
          notes: [
            'Cash Sales page for a consumer now shows the consumer name and correctly filters receipts to that consumer',
            'Moved Manage Consumer below Dashboard V2 in the sidebar and renamed Sold to Cash Sales',
            'Added a Cash Sales shortcut button on the Manage Consumers page',
            'Product Movement reversal rows now show their receipt no or consumer loan id instead of a raw id',
            'Removed the Notes column from Product Movement',
            'Fixed the Product Movement stock filter not accepting typed input',
            'Editing a sale without changing quantity, stock, or unit no longer creates reversal entries',
            'Credit sales are now recorded as stock-out in Product Movement',
            'Fixed the Inventory Monitoring Excel export server error',
            'Fixed wrong Qty Remaining values in Product Movement - each row now shows its product\'s true running balance regardless of filters or paging',
            'Inventory Monitoring\'s Quantity Remaining Breakdown dialog now uses the same server-computed balance as Product Movement, so both views match the actual inventory quantity',
            'Stock losses are now recorded in Product Movement when they happen - previously they only appeared after a full journal rebuild, making Qty Remaining read higher than the actual inventory',
            'Fixed editing a stock loss corrupting the inventory quantity when the loss quantity was increased',
            'Qty Remaining in Product Movement now converts pack units (e.g. 12-pcs) to the stock\'s base unit, so balances match the actual inventory quantity',
            'Inventory recalculation and date-filtered Inventory Monitoring now correctly subtract promo giveaway stock and convert pack units',
            'Inventory recalculation and date-filtered Inventory Monitoring now include returned products, with good and bad-order quantities split correctly',
            'Bad-order returns no longer double-count on the original product\'s movement history, and editing a return more than once no longer corrupts its journal entries',
            'Recalculate now derives quantities directly from the Product Movement journal, so Actual Qty, Product Movement, and the breakdown dialog always agree after a recalculate',
            'Fixed the Manage Consumer "With Loans Only" switch - it was filtering in reverse and required a manual refresh to apply',
            'Manage Consumer now paginates and filters on the server, so large consumer lists load fast and filters apply to the whole list',
            'Manage Consumer columns Name, Address, Balance, and Remarks are now sortable across all pages (Remarks sorts by urgency)',
          ],
        },
        {
          version: '1.19.2',
          date: '2026-07-03',
          notes: [
            'Added a toggle to switch the Most Sold Items dashboard chart between quantity and amount, including ranking',
            'Fixed Product Movement stock column showing raw IDs instead of product names for items from other stores',
            'Combined Manage Loans and Cash Sales into a single Sales button with a chooser page',
          ],
        },
        {
          version: '1.19.0',
          date: '2026-06-22',
          notes: [
            'Added inventory journal breakdown with running qty remaining balance',
            'Improved /stocks page search and UI',
            'Added returned products index with CRUD',
            'Fixed sale journal entries not appearing immediately after sale',
            'Improved journal type display with icons and colors',
            'Added create restore point and recalculate inventory actions',
          ],
        },
        {
          version: '1.18.9',
          date: '2026-05-01',
          notes: [
            'Hotfix: corrected inventory sync on multi-store transfers',
          ],
        },
      ],
    }
  },

  computed: {
    // The release being shipped: whatever is still flagged preRelease, or the
    // newest entry once nothing is pending
    currentReleases() {
      const pending = this.releases.filter((release) => release.preRelease)
      return pending.length ? pending : this.releases.slice(0, 1)
    },
    previousReleases() {
      return this.releases.filter(
        (release) => !this.currentReleases.includes(release),
      )
    },
    // Only badge a shipped release as Latest when it is the one being
    // featured — under "Previous Releases" the heading already says it is not
    latestRelease() {
      const featured = this.currentReleases[0]
      return featured && !featured.preRelease ? featured : null
    },
    sections() {
      const sections = [
        {
          key: 'current',
          title: 'This Update',
          icon: 'mdi-star-four-points',
          color: 'primary',
          textClass: 'primary--text',
          releases: this.currentReleases,
        },
      ]
      if (this.previousReleases.length) {
        sections.push({
          key: 'previous',
          title: 'Previous Releases',
          icon: 'mdi-history',
          color: 'grey',
          textClass: 'grey--text',
          releases: this.previousReleases,
        })
      }
      return sections
    },
  },

  methods: {
    isLatest(release) {
      return release === this.latestRelease
    },
  },
}
</script>
